import React, { useCallback, useEffect, useMemo, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Tab, Tabs, Typography } from "@mui/material";
import { debounce } from "lodash";
import type { SourceRow, TopicRow } from "store/types/news";

import type { FeedConfigBuckets } from "../../context/NewsFeedContext";
import { GuidancePanel } from "./GuidancePanel";
import { SourcesPanel } from "./SourcesPanel";
import { ibtnSx } from "./styles";
import { TopicsPanel } from "./TopicsPanel";

type Props = {
  topics: FeedConfigBuckets<TopicRow>;
  sources: FeedConfigBuckets<SourceRow>;
  guidance: string;
  onSaveGuidance: (guidance: string) => Promise<void>;
  onCreateTopic: (payload: TopicRow) => Promise<TopicRow>;
  onUpdateTopic: (payload: TopicRow) => Promise<TopicRow>;
  onDeleteTopic: (id: number) => Promise<void>;
  onSelectSystemTopic: (id: number) => Promise<void>;
  onUnselectSystemTopic: (id: number) => Promise<void>;
  onCreateSource: (payload: SourceRow) => Promise<SourceRow>;
  onUpdateSourcePriority: (source_url: string, priority: number, name: string) => Promise<SourceRow>;
  onDeleteSource: (source_url: string) => Promise<void>;
  onSelectSystemSource: (source_url: string, name: string) => Promise<void>;
  onUnselectSystemSource: (source_url: string) => Promise<void>;
  onCancel: () => void;
};

type TabKey = "topics" | "sources" | "guidance";

const SAVE_GUIDANCE_DEBOUNCE_MS = 500;

export const ConfigureFeedDialog: React.FC<Props> = ({
  topics,
  sources,
  guidance,
  onSaveGuidance,
  onCreateTopic,
  onUpdateTopic,
  onDeleteTopic,
  onSelectSystemTopic,
  onUnselectSystemTopic,
  onCreateSource,
  onUpdateSourcePriority,
  onDeleteSource,
  onSelectSystemSource,
  onUnselectSystemSource,
  onCancel
}) => {
  const [tab, setTab] = useState<TabKey>("topics");
  const [guidanceDraft, setGuidanceDraft] = useState(guidance);

  const debouncedSaveGuidance = useMemo(
    () =>
      debounce((text: string) => {
        onSaveGuidance(text);
      }, SAVE_GUIDANCE_DEBOUNCE_MS),
    [onSaveGuidance]
  );

  const handleGuidanceChange = useCallback(
    (next: string) => {
      setGuidanceDraft(next);
      debouncedSaveGuidance(next);
    },
    [debouncedSaveGuidance]
  );

  useEffect(
    () => () => {
      debouncedSaveGuidance.flush();
    },
    [debouncedSaveGuidance]
  );

  const handleCloseClick = useCallback(() => {
    debouncedSaveGuidance.flush();
    onCancel();
  }, [debouncedSaveGuidance, onCancel]);

  return (
    <Box
      sx={{
        width: "min(590px, 94vw)",
        maxWidth: 590,
        minWidth: "min(590px, 94vw)",
        maxHeight: "min(580px, 90vh)",
        display: "flex",
        flexDirection: "column" as const,
        boxSizing: "border-box" as const
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          px: "28px",
          pt: "24px",
          pb: "20px",
          borderBottom: `1px solid ${"#dbe8f5"}`,
          flexShrink: 0
        }}
      >
        <Typography component="div" sx={{ fontSize: 18, fontWeight: 500, color: "#071f35" }}>
          Configure feed
        </Typography>
        <IconButton aria-label="close" onClick={handleCloseClick} sx={ibtnSx}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        TabIndicatorProps={{ sx: { display: "none" } }}
        sx={{
          px: "24px",
          minHeight: 0,
          borderBottom: `1px solid ${"#dbe8f5"}`,
          flexShrink: 0,
          "& .MuiTabs-flexContainer": { gap: 0 },
          "& .MuiTab-root": {
            textTransform: "none",
            fontSize: 14,
            fontWeight: 400,
            color: "#5d7782",
            minHeight: 0,
            padding: "9px 11px 7px",
            borderBottom: "2px solid transparent",
            minWidth: 0,
            "&.Mui-selected": { color: "#071f35", fontWeight: 500, borderBottomColor: "#071f35" }
          }
        }}
      >
        <Tab label="Topics" value="topics" />
        <Tab label="Sources" value="sources" />
        <Tab label="Guidance" value="guidance" />
      </Tabs>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: "20px 28px" }}>
        <Box sx={{ display: tab === "topics" ? "block" : "none" }}>
          <TopicsPanel
            initialTopics={topics}
            onCreateTopic={onCreateTopic}
            onUpdateTopic={onUpdateTopic}
            onDeleteTopic={onDeleteTopic}
            onSelectSystemTopic={onSelectSystemTopic}
            onUnselectSystemTopic={onUnselectSystemTopic}
          />
        </Box>
        <Box sx={{ display: tab === "sources" ? "block" : "none" }}>
          <SourcesPanel
            initialSources={sources}
            onCreateSource={onCreateSource}
            onUpdateSourcePriority={onUpdateSourcePriority}
            onDeleteSource={onDeleteSource}
            onSelectSystemSource={onSelectSystemSource}
            onUnselectSystemSource={onUnselectSystemSource}
          />
        </Box>
        <Box sx={{ display: tab === "guidance" ? "block" : "none" }}>
          <GuidancePanel value={guidanceDraft} onChange={handleGuidanceChange} />
        </Box>
      </Box>

      <Box
        sx={{
          px: "28px",
          py: "16px",
          flexShrink: 0,
          display: "flex",
          justifyContent: "flex-end",
          gap: "8px"
        }}
      />
    </Box>
  );
};
